/**
 * Hook for fetching column aggregations for the totals row in a CRM table.
 *
 * Batches multiple column aggregation requests into a single API call,
 * using property_name + function as an alias for each aggregation.
 *
 * Disabled when search is active (same as useAggregateMetrics) since
 * the aggregation backend does not support search filtering.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getCrmAggregateQueryKey } from '@/lib/crm/hooks/useCrmAggregate';
import { apiPost } from '@/lib/http/client';
import type { CrmObjectFilters, FilterGroup } from '@/types/crm';

export type AggregationFunction =
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'count'
  | 'count_empty'
  | 'count_filled'
  | 'percent_empty'
  | 'percent_filled';

export interface ColumnAggregation {
  propertyName: string;
  function: AggregationFunction;
}

export type AggregationValue =
  | number
  | string
  | null
  | { amount: number; unit: string }
  | Array<{ amount: number; unit: string }>;

/** Shape returned by the aggregate API endpoint */
interface AggregateApiResponse {
  count: number;
  results: Array<{
    group_by: Record<string, unknown>;
    aggregations: Record<string, AggregationValue>;
  }>;
}

/** Normalized shape returned by the hook (flat aggregations map) */
export interface ColumnAggregationsResponse {
  aggregations: Record<string, AggregationValue>;
}

interface AggregationConfig {
  function: AggregationFunction;
  property?: string;
  alias: string;
}

interface AggregationRequest {
  entity_type_name: string;
  filter_groups?: FilterGroup[];
  convert_to?: string;
  aggregations: AggregationConfig[];
}

/**
 * Hook for fetching aggregated values for multiple columns in a single API call.
 *
 * Results are keyed by `${propertyName}_${function}` alias.
 *
 * @param orgId - The organization ID
 * @param entityTypeName - The entity type name (e.g., 'ahoy_deal')
 * @param aggregations - List of column aggregations to compute
 * @param filters - Optional filters to apply (same as object list)
 * @param convertTo - Optional currency code to convert monetary aggregations into.
 * When set, the backend returns a single `{amount, unit}` object instead of a
 * per-currency array for quantity aggregations.
 * @returns React Query result with aggregation values keyed by alias
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useColumnAggregations(
 *   orgId,
 *   'ahoy_deal',
 *   [{ propertyName: 'ahoy_amount', function: 'sum' }],
 *   filters
 * );
 * const value = data?.aggregations['ahoy_amount_sum'];
 * ```
 */
export function useColumnAggregations(
  orgId: number,
  entityTypeName: string,
  aggregations: ColumnAggregation[],
  filters?: CrmObjectFilters,
  convertTo?: string
) {
  // Disable when search is active — backend doesn't support search filtering,
  // so aggregation values would diverge from the filtered object list.
  const hasSearchFilter = !!filters?.search && filters.search.trim().length > 0;

  // Build requestBody outside queryFn so getCrmAggregateQueryKey produces a
  // stable key even when callers pass new array/object refs.
  const requestBody = useMemo(() => {
    const body: AggregationRequest = {
      entity_type_name: entityTypeName,
      aggregations: aggregations.map(agg => ({
        function: agg.function,
        property: agg.propertyName,
        alias: `${agg.propertyName}_${agg.function}`,
      })),
    };
    if (filters?.filter_groups && filters.filter_groups.length > 0) {
      body.filter_groups = filters.filter_groups;
    }
    if (convertTo) {
      body.convert_to = convertTo;
    }
    return body;
  }, [entityTypeName, aggregations, filters, convertTo]);

  const rawQuery = useQuery<AggregateApiResponse, Error>({
    // Use the shared crmAggregate key family so applyEvent.ts WS invalidation
    // covers column totals alongside dashboard and pipeline metrics.
    queryKey: getCrmAggregateQueryKey(orgId, requestBody),
    queryFn: async () => {
      return apiPost<AggregateApiResponse>(
        `/api/orgs/${orgId}/crm-events/crm-objects/aggregate/`,
        requestBody
      );
    },
    enabled: !!orgId && aggregations.length > 0 && !hasSearchFilter,
    staleTime: 5 * 60 * 1000,
    // Keep previous results visible while refetching (e.g. when a new
    // column aggregation is added) so only the new column shows a skeleton.
    // Disabled when search is active to avoid showing stale totals that
    // don't match the filtered list.
    placeholderData: hasSearchFilter ? undefined : prev => prev,
  });

  // Extract aggregations from the first (ungrouped) result.
  //
  // Mask the data while search is active. The query is disabled when
  // `hasSearchFilter` is true, but a disabled React Query retains its last
  // successful result — which here is the UNFILTERED totals. `search` is not
  // part of the query key (the aggregate backend can't filter by it), so
  // without this mask `TotalsRow` would show totals that don't match the
  // search-filtered object list. Returning undefined forces the totals row
  // into its empty/loading state instead of showing stale numbers.
  const data: ColumnAggregationsResponse | undefined =
    !hasSearchFilter && rawQuery.data
      ? { aggregations: rawQuery.data.results?.[0]?.aggregations ?? {} }
      : undefined;

  return {
    ...rawQuery,
    data,
  };
}
